

import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  Timestamp,
  query,
  orderBy,
  Firestore,
  DocumentData
} from "firebase/firestore";
import { CampOrganization } from "./campOrganizationTypes";

// Helper
const getCampOrganizationsRef = (db: Firestore) => collection(db, "campOrganizations");

/**
 * Maps a campOrganizations doc's data (Web SDK values) to the list row shape. Shared by the
 * /organizations browse list and admin search so both render the same fields the same way.
 */
export const mapCampOrganizationDocData = (id: string, data: DocumentData): CampOrganization => {
  const createdAt =
    data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString();
  const lastEditedAt =
    data.lastEditedAt instanceof Timestamp
      ? data.lastEditedAt.toDate().toISOString()
      : data.lastEditedAt || new Date().toISOString();
  // Normalize legacy visibility values to new schema
  const rawVisibility = data.visibility as any;
  const normalizedVisibility: 'active' | 'archived' =
    rawVisibility === 'v_archived' || rawVisibility === 'archived'
      ? 'archived'
      : 'active';

  return {
    id,
    name: data.name,
    slug: data.slug,
    canonicalLocationId:
      typeof data.canonicalLocationId === "string" ? data.canonicalLocationId : undefined,
    urlOrganization: data.urlOrganization,
    createdAt,
    lastEditedAt,
    lastEditedBy: data.lastEditedBy,
    visibility: normalizedVisibility,
    workflowStatus: data.workflowStatus,
    mailingAddress: data.mailingAddress,
    descAboutTheOrg: data.descAboutTheOrg,
    descAboutTheOrgCopiedFromUrl: data.descAboutTheOrgCopiedFromUrl,
    descStaffingAndLeadership: data.descStaffingAndLeadership,
    descStaffingAndLeadershipCopiedFromUrl: data.descStaffingAndLeadershipCopiedFromUrl,
    ein: data.ein,
    isNonProfit: data.isNonProfit !== undefined ? data.isNonProfit : null,
    isFranchise: data.isFranchise !== undefined ? data.isFranchise : null,
    isMunicipalGovt: data.isMunicipalGovt !== undefined ? data.isMunicipalGovt : null,
    yearOrgFounded: data.yearOrgFounded,
    yearDoingCampSince: data.yearDoingCampSince,
    numPublishedLocations: data.numPublishedLocations || 0,
    sessionsCount:
      typeof data.sessionsCount === "number" && !Number.isNaN(data.sessionsCount)
        ? data.sessionsCount
        : undefined,
    sessionsCountReconciledAt:
      data.sessionsCountReconciledAt instanceof Timestamp
        ? data.sessionsCountReconciledAt
        : undefined,
    contacts: data.contacts || []
  } as CampOrganization;
};

// GET ALL
export const getCampOrganizations = async (db: Firestore) => {
  try {
    const organizationsRef = getCampOrganizationsRef(db);
    // Keep Firestore order simple; UI will sort by lastEditedAt DESC
    const q = query(organizationsRef, orderBy("name"));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log("No camp organizations found");
      return [];
    }

    const organizations = querySnapshot.docs.map(docSnap => mapCampOrganizationDocData(docSnap.id, docSnap.data()));

    console.log(`Retrieved ${organizations.length} camp organizations`);
    return organizations;
  } catch (error) {
    console.error("Error getting camp organizations:", error);
    throw error;
  }
};

// GET ONE
export const getCampOrganization = async (db: Firestore, id: string) => {
  try {
    const docRef = doc(db, "campOrganizations", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const createdAt =
        data.createdAt instanceof Timestamp
          ? data.createdAt.toDate().toISOString()
          : data.createdAt || new Date().toISOString();
      const lastEditedAt =
        data.lastEditedAt instanceof Timestamp
          ? data.lastEditedAt.toDate().toISOString()
          : data.lastEditedAt || new Date().toISOString();
      return {
        id: docSnap.id,
        name: data.name,
        slug: data.slug,
        canonicalLocationId:
          typeof data.canonicalLocationId === "string" ? data.canonicalLocationId : undefined,
        urlOrganization: data.urlOrganization,
        createdAt,
        lastEditedAt,
        lastEditedBy: data.lastEditedBy,
        visibility: data.visibility,
        mailingAddress: data.mailingAddress,
        descAboutTheOrg: data.descAboutTheOrg,
        descAboutTheOrgCopiedFromUrl: data.descAboutTheOrgCopiedFromUrl,
        descStaffingAndLeadership: data.descStaffingAndLeadership,
        descStaffingAndLeadershipCopiedFromUrl: data.descStaffingAndLeadershipCopiedFromUrl,
        ein: data.ein,
        isNonProfit: data.isNonProfit !== undefined ? data.isNonProfit : null,
        isFranchise: data.isFranchise !== undefined ? data.isFranchise : null,
        isMunicipalGovt: data.isMunicipalGovt !== undefined ? data.isMunicipalGovt : null,
        yearOrgFounded: data.yearOrgFounded,
        yearDoingCampSince: data.yearDoingCampSince,
        numPublishedLocations: data.numPublishedLocations || 0,
        sessionsCount:
          typeof data.sessionsCount === "number" && !Number.isNaN(data.sessionsCount)
            ? data.sessionsCount
            : undefined,
        sessionsCountReconciledAt:
          data.sessionsCountReconciledAt instanceof Timestamp
            ? data.sessionsCountReconciledAt
            : undefined,
        contacts: data.contacts || [],
        sharedVariations: Array.isArray(data.sharedVariations) ? data.sharedVariations : []
      } as CampOrganization;
    } else {
      console.log("No camp organization found with ID:", id);
      return null;
    }
  } catch (error) {
    console.error("Error getting camp organization:", error);
    throw error;
  }
};

